import React, {useState, useCallback} from 'react'
import Popover from '@material-ui/core/Popover'
import {Editor} from '@tiptap/react'

const MAX_ROWS = 10
const MAX_COLS = 10

type TableGridPickerProps = {
  editor: Editor
  children: (props: {onClick: (e: React.MouseEvent) => void}) => React.ReactNode
}

export default function TableGridPicker({
  editor,
  children,
}: TableGridPickerProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [hoverRow, setHoverRow] = useState(0)
  const [hoverCol, setHoverCol] = useState(0)

  const handleOpen = useCallback((e: React.MouseEvent) => {
    setAnchorEl(e.currentTarget as HTMLElement)
    setHoverRow(0)
    setHoverCol(0)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleCellClick = useCallback(() => {
    if (hoverRow > 0 && hoverCol > 0) {
      editor
        .chain()
        .focus()
        .insertTable({rows: hoverRow, cols: hoverCol, withHeaderRow: true})
        .run()
      setAnchorEl(null)
    }
  }, [editor, hoverRow, hoverCol])

  const open = Boolean(anchorEl)

  return (
    <>
      {children({onClick: handleOpen})}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        disableRestoreFocus
      >
        <div className="html-editor-table-grid-picker">
          <div className="html-editor-table-grid-picker__grid">
            {Array.from({length: MAX_ROWS}, (_, row) => (
              <div key={row} className="html-editor-table-grid-picker__row">
                {Array.from({length: MAX_COLS}, (_, col) => (
                  <div
                    key={col}
                    className={`html-editor-table-grid-picker__cell${
                      row < hoverRow && col < hoverCol
                        ? ' html-editor-table-grid-picker__cell--highlighted'
                        : ''
                    }`}
                    onMouseEnter={() => {
                      setHoverRow(row + 1)
                      setHoverCol(col + 1)
                    }}
                    onClick={handleCellClick}
                  />
                ))}
              </div>
            ))}
          </div>
          <div className="html-editor-table-grid-picker__label">
            {hoverRow > 0 && hoverCol > 0
              ? `${hoverRow} × ${hoverCol}`
              : 'Insert Table'}
          </div>
        </div>
      </Popover>
    </>
  )
}
